import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { createListItem } from "../graphClient";

function FieldInput({ field, value, onChange }) {
  const baseClass =
    "w-full border border-slate-300 rounded-lg px-3 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1B3A5C] focus:border-transparent";

  if (field.type === "choice") {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={baseClass}
      >
        <option value="">Select…</option>
        {field.choices.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "dateTime") {
    return (
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={baseClass}
      />
    );
  }

  if (field.type === "number") {
    return (
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={baseClass}
        inputMode="decimal"
      />
    );
  }

  if (field.name === "Notes") {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={baseClass}
        rows={3}
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={baseClass}
    />
  );
}

function PreviewModal({ formData, schema, onConfirm, onCancel, submitting }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto p-5 shadow-xl">
        <h3 className="font-semibold text-slate-900 text-lg mb-4">
          Confirm submission
        </h3>
        <div className="space-y-2 mb-6">
          {schema
            .filter((f) => formData[f.name])
            .map((f) => (
              <div key={f.name} className="flex justify-between text-sm gap-3">
                <span className="text-slate-500">{f.label}</span>
                <span className="font-medium text-slate-900 text-right max-w-[60%] tabular-nums">
                  {f.type === "number" && formData[f.name]
                    ? `$${Number(formData[f.name]).toFixed(2)}`
                    : formData[f.name]}
                </span>
              </div>
            ))}
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 border border-slate-300 text-slate-700 py-3 rounded-xl font-medium text-base disabled:opacity-50 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 bg-[#1B3A5C] hover:bg-[#0E243B] text-white py-3 rounded-xl font-medium text-base disabled:opacity-50 transition-colors"
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RegisterForm({ listName, title, schema, onBack }) {
  const { instance, accounts } = useMsal();

  const initialData = {};
  schema.forEach((f) => {
    initialData[f.name] = "";
  });

  const [formData, setFormData] = useState(initialData);
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  function updateField(name, value) {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handleReview() {
    setError(null);
    setShowPreview(true);
  }

  function buildPayload() {
    const fields = {};
    schema.forEach((f) => {
      const val = formData[f.name];
      if (!val) return;
      if (f.type === "number") {
        fields[f.name] = parseFloat(val);
      } else if (f.type === "dateTime") {
        fields[f.name] = new Date(val).toISOString();
      } else {
        fields[f.name] = val;
      }
    });
    return fields;
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildPayload();
      await createListItem(instance, accounts[0], listName, payload);
      setSuccess(true);
      setShowPreview(false);
    } catch (err) {
      setError(err.message);
      setShowPreview(false);
    } finally {
      setSubmitting(false);
    }
  }

  function handleAddAnother() {
    setFormData(initialData);
    setSuccess(false);
    setError(null);
  }

  const requiredFields = schema.filter((f) => f.required);
  const allRequiredFilled = requiredFields.every((f) => formData[f.name]);

  if (success) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={onBack}
            className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
          >
            &larr; Back
          </button>
          <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
            {title}
          </h2>
        </div>
        <div className="bg-white rounded-2xl border border-green-200 p-6 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <p className="text-green-700 font-semibold text-lg mb-2">
            Item added
          </p>
          <p className="text-slate-500 text-sm mb-4">
            Successfully saved to {title}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleAddAnother}
              className="bg-[#1B3A5C] hover:bg-[#0E243B] text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
            >
              Add another
            </button>
            <button
              onClick={onBack}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
            >
              Back to registers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
        >
          &larr; Back
        </button>
        <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
          {title}
        </h2>
      </div>

      {error && (
        <div className="bg-white rounded-2xl border border-red-200 p-4 mb-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <p className="text-red-700 text-sm font-medium">Failed to submit</p>
          <p className="text-red-500 text-xs mt-1 break-all">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 space-y-4">
        {schema.map((field) => (
          <div key={field.name}>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <FieldInput
              field={field}
              value={formData[field.name]}
              onChange={(val) => updateField(field.name, val)}
            />
          </div>
        ))}
      </div>

      <button
        onClick={handleReview}
        disabled={!allRequiredFilled}
        className="w-full mt-6 bg-[#1B3A5C] hover:bg-[#0E243B] text-white py-3 rounded-xl font-medium text-base disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Review &amp; Submit
      </button>

      {showPreview && (
        <PreviewModal
          formData={formData}
          schema={schema}
          onConfirm={handleSubmit}
          onCancel={() => setShowPreview(false)}
          submitting={submitting}
        />
      )}
    </div>
  );
}
